import { colors, Stack, Tooltip, Typography } from '@ui-components/neo.tax';
import { ReactComponent as InfoCircle } from '@ui-components/neo.tax/dist/icons/info-circle.svg';
import { ReactComponent as WarningTriangle } from '@ui-components/neo.tax/dist/icons/warning-triangle.svg';
import { useMemo } from 'react';

import { TABLE_CELL_PLACEHOLDER } from '@neo.tax/shared';

import { getCapitalizationDateString } from './asc-projects-layout.utils';

const EMPTY_FRAGMENT = <></>;

export type AscProjectCapitalizationEndDateCellProps = {
  value: Date | null;
  extendedFrom: Date | null;
  predictedValue: Date | null;
  predictedReason: string | null;
  predictedDifferenceTooltip: string;
  isCapitalized: boolean;
  missingValueTooltip: string;
};

export const AscProjectCapitalizationEndDateCell = ({
  extendedFrom,
  isCapitalized,
  missingValueTooltip,
  predictedDifferenceTooltip,
  predictedReason,
  predictedValue,
  value,
}: AscProjectCapitalizationEndDateCellProps): React.ReactElement => {
  const { isMatchingPredictionDate, valueDateString } = useMemo(() => {
    if (!isCapitalized) {
      return {
        valueDateString: '',
        isMatchingPredictionDate: false,
      };
    }

    const valueStr = getCapitalizationDateString(value);
    const predictedStr = getCapitalizationDateString(predictedValue);
    return {
      valueDateString: valueStr,
      isMatchingPredictionDate: valueStr === predictedStr && valueStr !== '', // Ensure not both empty
    };
  }, [value, predictedValue, isCapitalized]);

  if (!isCapitalized) {
    // show nothing if not capitalized
    return EMPTY_FRAGMENT;
  }

  // Non-null extendedFrom means a sync automatically moved the end date forward from that
  // value; surface it so users know the date changed without their input.
  const extensionIndicator =
    value && extendedFrom ? (
      <Tooltip
        text={`Automatically extended from ${getCapitalizationDateString(extendedFrom)} by the latest data sync`}
      >
        <InfoCircle width={16} color={colors.gray[400]} data-testid="info-circle" />
      </Tooltip>
    ) : null;

  // if value is the predicted value, render the date with a tooltip for predicted end date reason
  if (value && predictedValue && isMatchingPredictionDate) {
    return (
      <Stack direction="row" alignItems="center" gap={2}>
        <Tooltip text={predictedReason}>
          <Typography color="inherit" textOverflow="truncate" fontSize="inherit">
            {valueDateString}
          </Typography>
        </Tooltip>
        {extensionIndicator}
      </Stack>
    );
  }

  const dateComponent = value ? (
    <Typography color="inherit" textOverflow="truncate" fontSize="inherit">
      {valueDateString}
    </Typography>
  ) : (
    // No end date is set, so don't surface the predicted reasoning here — it explains a
    // capitalization end date the user isn't seeing, which is misleading. Only the matching-date
    // branch above (where an end date exists) shows the reason.
    <Tooltip text={missingValueTooltip}>{TABLE_CELL_PLACEHOLDER}</Tooltip>
  );

  const predictedDateWarning =
    predictedValue && !isMatchingPredictionDate ? (
      <Tooltip text={predictedDifferenceTooltip}>
        <WarningTriangle width={16} color={colors.lemon[400]} data-testid="warning-triangle" />
      </Tooltip>
    ) : null;

  return (
    <Stack direction="row" alignItems="center" gap={2}>
      {dateComponent} {extensionIndicator} {predictedDateWarning}
    </Stack>
  );
};
